import React from "react";

type FunnelColor =
  | "blue"
  | "violet"
  | "teal"
  | "green"
  | "orange"
  | "red"
  | "yellow"
  | "dark_orange"
  | "light_orange";

interface FunnelStep {
  label?: string;
  value: number;
  color: FunnelColor;
}

interface BadgeProps {
  step: FunnelStep;
  centerX: number;
  centerY: number;
}

interface FunnelChartProps {
  steps: FunnelStep[];
  height?: number;
  className?: string;
  renderBadge?: (props: BadgeProps) => React.ReactNode;
}

const colorMap: Record<FunnelColor, string> = {
  blue: "rgb(37, 99, 235)",
  violet: "rgb(124, 58, 237)",
  teal: "rgb(45, 212, 191)",
  green: "rgb(34, 197, 94)",
  red: "rgb(239, 68, 68)",
  yellow: "rgb(234, 179, 8)",
  dark_orange: "#FE691E",
  orange: "#FF8A05",
  light_orange: "#FFA203",
};

const DefaultBadge: React.FC<BadgeProps> = ({ step, centerX, centerY }) => {
  const color = colorMap[step.color];
  return (
    <foreignObject x={centerX - 40} y={centerY - 20} width={80} height={40}>
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "var(--sidebar)",
            border: `1px solid var(--border)`,
            borderRadius: "var(--radius-md)",
            padding: "1px 6px",
            fontSize: 6,
            fontWeight: 400,
            color,
          }}
        >
          <div className="font-mono">
            {step.value.toFixed(1)}%
            <span className="ml-1 text-foreground font-normal">
              {step.label}
            </span>
          </div>
        </div>
      </div>
    </foreignObject>
  );
};

const FunnelChart: React.FC<FunnelChartProps> = ({
  steps,
  height = 384,
  className = "",
  renderBadge,
}) => {
  const viewBoxWidth = 612;
  const viewBoxHeight = height;
  const stepWidth = viewBoxWidth / steps.length;

  const generatePath = (
    index: number,
    value: number,
    nextValue: number,
    opacity: number
  ): string => {
    const x1 = index * stepWidth;
    const x2 = (index + 1) * stepWidth;

    const centerY = viewBoxHeight / 2;
    const maxHeight = viewBoxHeight * 0.7;

    // Adjusted offsets for 4 layers
    const offsetBase =
      opacity === 1 ? 0 : opacity === 0.3 ? 3 : opacity === 0.15 ? 6 : 10;

    const leftHeight = (value / 100) * maxHeight;
    const rightHeight = (nextValue / 100) * maxHeight;

    const leftTop = centerY - leftHeight / 2 - offsetBase;
    const leftBottom = centerY + leftHeight / 2 + offsetBase;
    const rightTop = centerY - rightHeight / 2 - offsetBase;
    const rightBottom = centerY + rightHeight / 2 + offsetBase;

    const midX1 = x1 + stepWidth * 0.3;
    const midX2 = x1 + stepWidth * 0.7;

    return `M${x1},${leftBottom}
      C${midX1},${leftBottom} ${midX2},${rightBottom} ${x2},${rightBottom}
      L${x2},${rightTop}
      C${midX2},${rightTop} ${midX1},${leftTop} ${x1},${leftTop}
      Z`;
  };

  const Badge = renderBadge || DefaultBadge;

  return (
    <svg
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      className={`w-full h-auto ${className}`}
      preserveAspectRatio="xMidYMid meet"
    >
      {steps.map((step, index) => {
        const nextValue =
          index < steps.length - 1 ? steps[index + 1].value : step.value;
        const centerX = index * stepWidth + stepWidth / 2;

        return (
          <g key={step.label || index}>
            <rect
              x={index * stepWidth}
              y={0}
              width={stepWidth}
              height={viewBoxHeight}
              className="fill-transparent transition-colors"
            />

            {index !== 0 && (
              <line
                x1={index * stepWidth}
                y1={0}
                x2={index * stepWidth}
                y2={viewBoxHeight}
                strokeWidth={0.5}
                strokeDasharray="2 2"
                className="stroke-border"
              />
            )}

            {[1, 0.3, 0.15, 0.08].map((opacity) => (
              <path
                key={opacity}
                d={generatePath(index, step.value, nextValue, opacity)}
                fill={colorMap[step.color]}
                opacity={opacity}
              />
            ))}

            <Badge step={step} centerX={centerX} centerY={viewBoxHeight / 2} />
          </g>
        );
      })}
    </svg>
  );
};

export default FunnelChart;
export { DefaultBadge, colorMap };
export type { FunnelStep, FunnelChartProps, FunnelColor, BadgeProps };
